import AdmissionItem from '@/components/booking-summary/AdmissionItem';
import { TransportationTypeIcon } from '@/components/icons/TransportationTypeIcon';
import { TransText } from '@/i18n/trans/text';
import type { AdmissionDTO, BookedTripDTO, FeeDTO } from '@/types/dto/agent';
import type { CheckoutOrBookingPassengerMap } from '@/types/passenger';
import { cn } from '@/utils/cn';
import { formatDay, formatTime } from '@/utils/dateTime';
import { getPassengerName } from '@/utils/passenger';
import { Divider, Icons, Typography } from '@fleet/webshop-ui';
import type { FC } from 'react';

interface TripsProps {
  trips: Array<BookedTripDTO>;
  passengersMap: CheckoutOrBookingPassengerMap;
  reservationFeesMap: Record<NonNullable<FeeDTO['id']>, FeeDTO>;
  direction: 'outbound' | 'inbound';
}

const Trips: FC<TripsProps> = ({
  trips,
  passengersMap,
  reservationFeesMap,
  direction,
}) => {
  const grayedOutStatuses: Array<AdmissionDTO['status']> = [
    'REFUNDED',
    'RELEASED',
    'EXCHANGED',
  ];
  let passengerIndex = -1;

  return (
    <>
      <div
        className={cn('flex gap-2 bg-primary-pale px-4 py-3', {
          ['rounded-t-lg']: direction === 'outbound',
        })}
      >
        {/* FIXME: transportReturn and transportFrom default icon sizes dont match */}
        {direction === 'outbound' ? (
          <Icons.transportFrom className="h-5 w-5 text-primary" />
        ) : (
          <Icons.transportReturn className="h-5 w-5 text-primary" />
        )}
        <Typography variant="body1-bold">
          <TransText
            i18nKey={direction === 'outbound' ? 'outboundTrip' : 'returnTrip'}
          />
        </Typography>
      </div>
      {trips.map((trip, key) => (
        <div className="px-4" key={`${trip.id}-key`}>
          {key !== 0 && <Divider />}
          {trip.legs?.map((leg, key) => {
            const isLegGrayedOut = trip.bookedOffers?.every((offer) =>
              offer.admissions?.every(
                (admission) =>
                  grayedOutStatuses.includes(admission.status) &&
                  admission.ancillaries?.every((ancillary) =>
                    grayedOutStatuses.includes(ancillary.status)
                  )
              )
            );

            return (
              <div className="py-3" key={`${leg.id}-${key}`}>
                <div className="grid w-full grid-cols-[20px,1fr,40px,40px]">
                  <TransportationTypeIcon
                    ptMode={leg.ptMode}
                    className={cn('text-primary', {
                      'text-neutral': isLegGrayedOut,
                    })}
                  />
                  <Typography
                    variant="body2-bold"
                    className={cn({
                      'text-neutral': isLegGrayedOut,
                    })}
                  >
                    {leg.originStop?.name}
                  </Typography>
                  <Typography variant="body2" className="text-neutral">
                    {formatDay(leg.departureTime)}
                  </Typography>
                  <Typography
                    variant="body2"
                    className={cn('text-right', {
                      'text-neutral': isLegGrayedOut,
                    })}
                  >
                    {formatTime(leg.departureTime)}
                  </Typography>
                </div>

                <div className="ml-[7px] h-3 w-0.5 bg-neutral-light" />

                <div className="grid w-full grid-cols-[20px,1fr,40px,40px]">
                  <Icons.destination
                    height={12}
                    width={12}
                    className={cn('ml-0.5 text-primary', {
                      'text-neutral': isLegGrayedOut,
                    })}
                  />
                  <Typography
                    variant="body2-bold"
                    className={cn({
                      'text-neutral': isLegGrayedOut,
                    })}
                  >
                    {leg.destinationStop?.name}
                  </Typography>
                  <Typography variant="body2" className="text-neutral">
                    {formatDay(leg.arrivalTime)}
                  </Typography>
                  <Typography
                    variant="body2"
                    className={cn('text-right', {
                      'text-neutral': isLegGrayedOut,
                    })}
                  >
                    {formatTime(leg.arrivalTime)}
                  </Typography>
                </div>
                {trip.bookedOffers?.map((offer) =>
                  offer.admissions?.map((admission, key) => {
                    if (leg.id && !admission.coveredLegIds?.includes(leg.id)) {
                      return null;
                    }

                    const isAdmissionGrayedOut =
                      grayedOutStatuses.includes(admission.status) &&
                      admission.ancillaries?.every((ancillary) =>
                        grayedOutStatuses.includes(ancillary.status)
                      );

                    return (
                      <div
                        className="mt-4 flex flex-col gap-4"
                        key={`${admission.id}-${key}`}
                      >
                        {admission.passengerIds?.map((id, key) => {
                          const passenger = passengersMap[id];

                          if (!passenger) {
                            return null;
                          }

                          passengerIndex++;

                          return (
                            <div key={`${id}-${key}`}>
                              <div className="flex items-center justify-between">
                                <Typography
                                  variant="body2-bold"
                                  className={cn({
                                    'text-neutral': isAdmissionGrayedOut,
                                  })}
                                >
                                  {getPassengerName(
                                    {
                                      firstName: passenger.firstName,
                                      lastName: passenger.lastName,
                                    },
                                    passengerIndex
                                  )}
                                </Typography>
                                {admission.reservations?.map(
                                  (reservation, key) => {
                                    const reservedPlace =
                                      reservation.placeAllocations
                                        ?.reservedPlaces?.[0];

                                    if (isAdmissionGrayedOut) {
                                      return null;
                                    }

                                    return (
                                      <div
                                        className="flex gap-2"
                                        key={`${reservation.id}-${key}`}
                                      >
                                        {leg.ptMode?.code !== 'BUS' &&
                                          reservedPlace?.coachNumber && (
                                            <div className="flex items-center gap-0.5">
                                              <Icons.carriage className="h-3 w-3 text-neutral" />
                                              <Typography
                                                variant="body2-bold"
                                                className="text-neutral"
                                              >
                                                {reservedPlace.coachNumber}
                                              </Typography>
                                            </div>
                                          )}
                                        {reservedPlace?.placeNumber && (
                                          <div className="flex items-center gap-0.5">
                                            <Icons.seat className="h-3 w-3 text-neutral" />
                                            <Typography
                                              variant="body2-bold"
                                              className="text-neutral"
                                            >
                                              {reservedPlace.placeNumber}
                                            </Typography>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                              <div className="ml-6 mt-2 flex flex-col gap-1">
                                <AdmissionItem
                                  icon="ticket"
                                  description={
                                    admission.productSummary ??
                                    admission.summary
                                  }
                                  descriptiveTexts={
                                    admission.productDescriptiveTexts
                                  }
                                  amount={
                                    (admission.price?.amount || 0) +
                                    (admission.reservations || []).reduce(
                                      (acc, { price }) =>
                                        acc + (price?.amount || 0),
                                      0
                                    )
                                  }
                                  status={admission.status}
                                  promoCode={admission.appliedReductions?.[0]}
                                />

                                {admission.ancillaries?.map(
                                  (ancillary, key) => (
                                    <AdmissionItem
                                      key={`${ancillary.id}-${key}`}
                                      icon="addon"
                                      description={ancillary.productDescription}
                                      amount={ancillary.price?.amount || 0}
                                      status={ancillary.status}
                                    />
                                  )
                                )}
                                {admission.reservations?.map((reservation) =>
                                  reservation.feeRefs?.map((feeRef, key) => {
                                    const fee = reservationFeesMap[feeRef];

                                    if (!fee || !fee.type || !fee.status) {
                                      return null;
                                    }

                                    return (
                                      <AdmissionItem
                                        key={`${fee.id}-${key}`}
                                        icon="seat"
                                        description={
                                          <TransText i18nKey={fee.type} />
                                        }
                                        amount={fee.price?.amount || 0}
                                        status={admission.status}
                                      />
                                    );
                                  })
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })
                )}
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
};

export default Trips;
